import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api";
import { Modal } from "../components/ui";
import EmailComposer from "../components/EmailComposer";
import WaDraftModal from "../components/WaDraftModal";
import { openComposerWindow, onMailChanged } from "../composerWindow";
import usePersistedState from "../hooks/usePersistedState";
import DateIT from "../components/DateIT";
import SenderCard from "../components/SenderCard";

const GENERIC_DOMAINS = new Set(["gmail.com", "googlemail.com", "hotmail.com", "hotmail.it", "libero.it", "yahoo.com", "yahoo.it", "outlook.com", "outlook.it", "live.it", "live.com", "icloud.com", "alice.it", "tin.it", "virgilio.it", "tiscali.it", "pec.it", "aruba.it", "pec.aruba.it", "legalmail.it"]);

function fmt(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString("it-IT") + " " + dt.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

export default function Inbox() {
  const [tab, setTab] = usePersistedState("inbox.tab", "da_gestire");
  const [soloDomande, setSoloDomande] = useState(false);
  const [channel, setChannel] = usePersistedState("inbox.channel", "");
  const [relation, setRelation] = usePersistedState("inbox.relation", "");
  const [hasAtt, setHasAtt] = usePersistedState("inbox.hasAtt", false);
  const [unassigned, setUnassigned] = usePersistedState("inbox.unassigned", false);
  const [counts, setCounts] = useState(null);
  const [q, setQ] = usePersistedState("inbox.q", "");
  const [qInput, setQInput] = useState("");
  const [sp] = useSearchParams();
  useEffect(() => { const c = sp.get("cerca"); setQInput(c || q); }, []); // eslint-disable-line
  const [accountF, setAccountF] = useState("");
  const [accCounts, setAccCounts] = useState([]);
  const [openMsg, setOpenMsg] = useState(null);
  const loadAccCounts = useCallback(() => {
    api.get("/emails/accounts-counts").then((r) => setAccCounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadAccCounts(); }, [loadAccCounts]);
  const pushedRef = useRef(false);
  // Vista 3 colonne rimossa su richiesta: il click apre sempre la pagina completa dell'email
  const openInline = (m) => { window.location.href = `/email/${m.id}`; };
  const closeDetail = useCallback(() => {
    setOpenMsg(null);
    if (pushedRef.current) { pushedRef.current = false; window.history.back(); }
  }, []);
  useEffect(() => {
    const onPop = () => { if (pushedRef.current) { pushedRef.current = false; setOpenMsg(null); } };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [msgModal, setMsgModal] = useState(null); // "reply" | "replyall" | "forward"
  const [evt, setEvt] = useState(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockScope, setBlockScope] = useState("domain");
  const [workTime, setWorkTime] = useState(null);
  const openWorkTime = () => {
    const now = new Date();
    setWorkTime({
      company_id: openMsg.company_id || "",
      date: now.toISOString().slice(0, 10),
      time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`,
      dur: 30,
      title: `Lavorazione · ${openMsg.subject || openMsg.from_name || openMsg.from_email}`.slice(0, 120),
    });
  };
  const saveWorkTime = async () => {
    try {
      await api.post("/appointments?force=1", {
        date: workTime.date, time: workTime.time, dur: Number(workTime.dur) || 30,
        title: workTime.title, name: openMsg.from_name || openMsg.from_email,
        company_id: workTime.company_id, mode: "studio", category: "studio",
        count_hours: true,
      });
      const cname = (allCompanies.find((c) => c.id === workTime.company_id) || {}).name || "il cliente";
      setWorkTime(null);
      alert(`⏱ Tempo registrato (${workTime.dur} min) per ${cname}: verrà conteggiato nelle ore e nel margine del cliente.`);
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };
  const openEvent = () => {
    const m = openMsg;
    setEvt({ title: m.subject ? `Appuntamento · ${m.subject}` : `Appuntamento con ${m.from_name || m.from_email}`, date: "", time: "10:00", dur: 30, target: "crm", invite: false, desc: "" });
  };
  const saveEvent = async () => {
    const m = openMsg; const isWa = m.channel === "whatsapp";
    try {
      if (evt.target === "crm" || evt.target === "both") {
        await api.post("/appointments", {
          date: evt.date, time: evt.time, dur: Number(evt.dur) || 30, title: evt.title,
          name: m.from_name || m.from_email,
          email: !isWa && m.from_email ? m.from_email : null,
          phone: isWa ? m.from_email : null,
          mode: "presenza", category: "cliente",
        });
      }
      if (evt.target === "google" || evt.target === "both") {
        await api.post("/calendar/events", {
          summary: evt.title,
          description: evt.desc || `Creato dall'email di ${m.from_email}`,
          start: `${evt.date}T${evt.time}:00`,
          duration_min: Number(evt.dur) || 30,
          attendees: evt.invite && !isWa && m.from_email ? [m.from_email] : [],
        });
      }
      setEvt(null);
      alert("Evento creato nel calendario ✔");
    } catch (e) { alert("Errore: " + (e?.response?.data?.detail || e.message)); }
  };
  const confirmBlock = async () => {
    const domain = (openMsg.from_email || "").split("@")[1] || "";
    const useDomain = blockScope === "domain" && domain && !GENERIC_DOMAINS.has(domain.toLowerCase());
    try {
      const r = await api.post("/exclusion-rules", {
        value: useDomain ? domain : openMsg.from_email,
        match_type: useDomain ? "domain" : "email",
      });
      setBlockOpen(false);
      alert(`⛔ Bloccato ${useDomain ? "@" + domain : openMsg.from_email}: ${r.data.emails_eliminate} email eliminate dal CRM. I messaggi futuri saranno bloccati automaticamente.`);
      closeDetail(); load(); loadAccCounts();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const downloadAtt = async (a) => {
    try {
      const r = await api.get(`/emails/${openMsg.id}/attachments/${a.index}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const el = document.createElement("a"); el.href = url; el.download = a.filename || "allegato"; el.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert("Errore download: " + (e.response?.data?.detail || e.message)); }
  };
  const [suggs, setSuggs] = useState([]);
  useEffect(() => { api.get("/emails/link-suggestions").then((r) => setSuggs(r.data.items || [])).catch(() => {}); }, []);
  const acceptSugg = async (s) => {
    try {
      const r = await api.post(`/companies/${s.company_id}/sender-links`, { pattern: s.pattern, note: "suggerito da Elon" });
      alert(`✅ "${r.data.pattern}" collegato a ${s.company}. Email ricollegate: ${r.data.email_ricollegate}.`);
      setSuggs((prev) => prev.filter((x) => x.pattern !== s.pattern));
      api.get("/emails/link-suggestions?refresh=1").catch(() => {});
      load();
    } catch (e) { alert(e?.response?.data?.detail || e.message); }
  };
  const ignoreSugg = async (s) => {
    await api.post("/emails/link-suggestions/ignore", { pattern: s.pattern }).catch(() => {});
    setSuggs((prev) => prev.filter((x) => x.pattern !== s.pattern));
  };
  useEffect(() => { const t = setTimeout(() => setQ(qInput), 400); return () => clearTimeout(t); }, [qInput, setQ]);
  const [sender, setSender] = usePersistedState("inbox.sender", "");
  const [recipient, setRecipient] = usePersistedState("inbox.recipient", "");
  const [dateFrom, setDateFrom] = usePersistedState("inbox.dateFrom", "");
  const [dateTo, setDateTo] = usePersistedState("inbox.dateTo", "");
  const [list, setList] = useState([]);
  const [syncMsg, setSyncMsg] = useState("");
  const [since, setSince] = useState("2026-01-01");
  const [taskModal, setTaskModal] = useState(null); // {email, title, due, note}
  const [selected, setSelected] = useState(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [starredF, setStarredF] = usePersistedState("inbox.starredF", false);
  const [unreadF, setUnreadF] = usePersistedState("inbox.unreadF", false);
  const [compose, setCompose] = useState(false);
  const [drafts, setDrafts] = useState(null); // null = modale chiusa
  const [draftCount, setDraftCount] = useState(0);
  const [resumeDraft, setResumeDraft] = useState(null);
  const refreshDraftCount = () => api.get("/emails/drafts").then((r) => setDraftCount(r.data.length)).catch(() => {});
  useEffect(() => { refreshDraftCount(); }, [compose, resumeDraft]); // eslint-disable-line
  // la finestra separata ha salvato o inviato: riallineo il contatore bozze
  useEffect(() => onMailChanged(() => refreshDraftCount()), []); // eslint-disable-line
  const openDrafts = async () => {
    try { const r = await api.get("/emails/drafts"); setDrafts(r.data); }
    catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };
  const deleteDraft = async (id) => {
    if (!window.confirm("Eliminare la bozza?")) return;
    await api.delete(`/emails/drafts/${id}`);
    setDrafts((d) => (d || []).filter((x) => x.id !== id));
    setDraftCount((n) => Math.max(0, n - 1));
  };
  const [companyF, setCompanyF] = usePersistedState("inbox.companyF", "");
  const [allCompanies, setAllCompanies] = useState([]);
  const [sentList, setSentList] = useState([]);
  const [visible, setVisible] = useState(50);
  useEffect(() => { setVisible(50); }, [list]);
  useEffect(() => { api.get("/companies").then((r) => setAllCompanies(r.data)).catch(() => {}); }, []);

  const reqRef = useRef(0);
  const load = useCallback(() => {
    const rid = ++reqRef.current;
    const p = new URLSearchParams({ status: tab });
    if (accountF) p.set("account", accountF);
    if (channel) p.set("channel", channel);
    if (relation) p.set("relation", relation);
    if (hasAtt) p.set("has_attachments", "true");
    if (unassigned) p.set("unassigned", "true");
    if (starredF) p.set("starred", "true");
    if (unreadF) p.set("unread", "true");
    if (soloDomande) p.set("solo_domande", "true");
    if (q) p.set("q", q);
    if (sender) p.set("sender", sender);
    if (recipient) p.set("recipient", recipient);
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    if (companyF) p.set("company", companyF);
    api.get(`/emails?${p.toString()}`).then((r) => { if (rid === reqRef.current) setList(r.data); }).catch(() => {});
    if (companyF) api.get(`/emails/sent?company=${companyF}`).then((r) => setSentList(r.data)).catch(() => setSentList([]));
    else setSentList([]);
    setSelected(new Set());
  }, [tab, channel, relation, hasAtt, unassigned, starredF, unreadF, soloDomande, q, sender, recipient, dateFrom, dateTo, companyF, accountF]);

  const loadCounts = useCallback(() => {
    const p = new URLSearchParams({ status: tab });
    if (channel) p.set("channel", channel);
    api.get(`/emails/counts?${p.toString()}`).then((r) => setCounts(r.data)).catch(() => {});
  }, [tab, channel]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Auto-aggiornamento ogni 60s (aggiorna lista e conteggi senza perdere la selezione)
  useEffect(() => {
    const refresh = () => {
      const rid = ++reqRef.current;
      const p = new URLSearchParams({ status: tab });
      if (accountF) p.set("account", accountF);
      if (channel) p.set("channel", channel);
      if (relation) p.set("relation", relation);
      if (hasAtt) p.set("has_attachments", "true");
      if (unassigned) p.set("unassigned", "true");
      if (starredF) p.set("starred", "true");
      if (unreadF) p.set("unread", "true");
      if (soloDomande) p.set("solo_domande", "true");
      if (q) p.set("q", q);
      if (sender) p.set("sender", sender);
      if (recipient) p.set("recipient", recipient);
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
      if (companyF) p.set("company", companyF);
      api.get(`/emails?${p.toString()}`).then((r) => { if (rid === reqRef.current) setList(r.data); }).catch(() => {});
      if (companyF) api.get(`/emails/sent?company=${companyF}`).then((r) => setSentList(r.data)).catch(() => {});
      loadCounts();
      loadAccCounts();
    };
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [tab, channel, relation, hasAtt, unassigned, starredF, unreadF, soloDomande, q, sender, recipient, dateFrom, dateTo, companyF, accountF, loadCounts, loadAccCounts]);

  const sync = async () => {
    setSyncMsg("Sincronizzazione…");
    try {
      const r = await api.post("/emails/sync");
      setSyncMsg(r.data.configurato ? `Ricevute ${r.data.ricevute}, inserite ${r.data.inserite}` : "Gmail non ancora collegato (nessuna mail scaricata)");
      load(); loadCounts();
    } catch (e) { setSyncMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const importHistory = async () => {
    if (!window.confirm("Tutte le email non ancora presenti nel CRM dalla data scelta finiranno in «Da gestire» (lo stato letta/non letta su Gmail viene ignorato). Procedere?")) return;
    setSyncMsg("Import storico avviato…");
    try {
      const r = await api.post("/emails/import-history", { since });
      if (!r.data.avviato && r.data.running) { setSyncMsg("Un import storico è già in corso…"); }
      const t = setInterval(async () => {
        try {
          const s = (await api.get("/emails/import-history/status", { noCache: true })).data;
          if (s.running) { setSyncMsg(`Import storico in corso… ${s.in_da_gestire || 0} importate`); return; }
          clearInterval(t);
          setSyncMsg(s.error ? `Errore import storico: ${s.error}` : `Storico completato: ${s.in_da_gestire || 0} email importate in Da gestire (dal ${s.since})`);
          load(); loadCounts();
        } catch { clearInterval(t); }
      }, 4000);
    } catch (e) { setSyncMsg("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const openTask = (m) => setTaskModal({ email: m, title: m.subject ? `Rispondere: ${m.subject}` : "", due: "", note: "" });

  const handleMsg = async (m) => {
    try { await api.post(`/emails/${m.id}/handle`); load(); loadCounts(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const reopenMsg = async (m) => {
    try { await api.post(`/emails/${m.id}/reopen`); load(); loadCounts(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const deleteMsg = async (m) => {
    if (!window.confirm("Eliminare definitivamente questo messaggio dal CRM?")) return;
    try { await api.delete(`/emails/${m.id}`); load(); loadCounts(); }
    catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = list.length > 0 && selected.size === list.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(list.map((m) => m.id)));
  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    if (action === "delete" && !window.confirm(`Eliminare definitivamente ${ids.length} messaggi dal CRM?`)) return;
    try {
      const r = await api.post("/emails/bulk", { ids, action });
      setSyncMsg(`${r.data.affected} messaggi aggiornati.`);
      load(); loadCounts();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const archiveOthers = async () => {
    const ids = list.filter((m) => !m.is_client).map((m) => m.id);
    if (!ids.length) { alert("Nessuna email 'Altre' da archiviare in questa vista."); return; }
    if (!window.confirm(`Segnare come gestite ${ids.length} email NON di clienti (newsletter/notifiche/sconosciuti) presenti in questa vista?`)) return;
    try {
      const r = await api.post("/emails/bulk", { ids, action: "handle" });
      setSyncMsg(`${r.data.affected} email archiviate.`);
      load(); loadCounts();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };


  const domainOf = (email) => (email || "").split("@")[1] || email || "";
  const domainGroups = (() => {
    const g = {};
    list.forEach((m) => { const d = domainOf(m.from_email); if (!d) return; g[d] = (g[d] || 0) + 1; });
    return Object.entries(g).sort((a, b) => b[1] - a[1]);
  })();
  const [domainPick, setDomainPick] = useState("");
  const selectDomain = (dom) => {
    if (!dom) return;
    setSelected(new Set(list.filter((m) => domainOf(m.from_email) === dom).map((m) => m.id)));
  };

  // abbina a cartella (massa)
  const [assignOpen, setAssignOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selCompany, setSelCompany] = useState("");
  const [selProject, setSelProject] = useState("");
  const [selFolder, setSelFolder] = useState("");
  const [showClosedF, setShowClosedF] = useState(false);
  const openAssign = () => {
    setAssignOneId(null);
    setAssignOpen(true); setSelCompany(""); setSelProject(""); setSelFolder(""); setProjects([]); setFolders([]);
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  };
  const [assignOneId, setAssignOneId] = useState(null);
  const openAssignOne = () => {
    setAssignOneId(openMsg.id);
    setAssignOpen(true); setSelProject(""); setSelFolder(""); setProjects([]); setFolders([]);
    setSelCompany(openMsg.company_id || "");
    if (openMsg.company_id) onCompany(openMsg.company_id);
    api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {});
  };
  const onCompany = (cid) => { setSelCompany(cid); setSelProject(""); setSelFolder(""); setFolders([]); if (cid) api.get(`/projects?company_id=${cid}`).then((r) => setProjects(r.data)).catch(() => {}); };
  const onProject = (pid) => { setSelProject(pid); setSelFolder(""); if (pid) api.get(`/projects/${pid}`).then((r) => setFolders(r.data.folders)).catch(() => {}); };
  const confirmAssign = async () => {
    if (!selFolder) { alert("Seleziona una cartella"); return; }
    try {
      if (assignOneId) {
        await api.post(`/emails/${assignOneId}/assign`, { folder_id: selFolder });
        setAssignOpen(false); setAssignOneId(null);
        setSyncMsg("Email abbinata alla cartella (gestita).");
        closeDetail();
      } else {
        const r = await api.post("/emails/bulk-assign", { ids: [...selected], folder_id: selFolder });
        setAssignOpen(false);
        setSyncMsg(`${r.data.affected} email abbinate alla cartella.`);
      }
      load(); loadCounts();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const toggleStar = async () => {
    const v = !openMsg.starred;
    try {
      await api.post(`/emails/${openMsg.id}/star`, { starred: v });
      setOpenMsg({ ...openMsg, starred: v });
      setList((prev) => prev.map((x) => (x.id === openMsg.id ? { ...x, starred: v } : x)));
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const createLead = async () => {
    const isWa = openMsg.channel === "whatsapp";
    try {
      const r = await api.post("/pipeline", {
        email: isWa ? null : openMsg.from_email,
        phone: isWa ? openMsg.from_email : null,
        name: openMsg.from_name || "",
        note: `Creato da messaggio in Da gestire: ${openMsg.subject || "(senza oggetto)"}`,
      });
      if (window.confirm("🎯 Lead creato. Aprire la sua scheda?")) window.location.href = `/lead/${r.data.id}`;
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  const saveTask = async () => {
    const { email, title, due, note } = taskModal;
    try {
      await api.post("/tasks", { title, due_date: due || null, description: note || null, email_id: email.id, company_id: email.company_id || null });
      if (note && note.trim()) {
        try { await api.put(`/emails/${email.id}/notes`, { notes: note }); } catch (err) { console.error("Errore salvataggio nota email:", err); }
      }
      setTaskModal(null);
      setSyncMsg("Incarico creato.");
    } catch (e) { alert("Errore: " + (e.response?.data?.detail || e.message)); }
  };

  const dayLabel = (d) => {
    if (!d) return "Senza data";
    const dt = new Date(d);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const that = new Date(dt); that.setHours(0, 0, 0, 0);
    const diff = Math.round((today - that) / 86400000);
    if (diff === 0) return "OGGI";
    if (diff === 1) return "IERI";
    return dt.toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });
  };

  const displayList = companyF
    ? [
        ...list.map((m) => ({ ...m, _dir: "in" })),
        ...sentList.map((s) => ({ ...s, id: `sent-${s.id}`, _dir: "out" })),
      ].sort((a, b) => new Date(b._dir === "out" ? b.sent_at : b.received_at) - new Date(a._dir === "out" ? a.sent_at : a.received_at))
    : list;

  return (
    <div>
      <div className="inbox-sticky" data-testid="inbox-sticky-header">
      <div className="toolbar" style={{ flexWrap: "wrap" }}>
        <h1 className="page-title" style={{ fontSize: 19, margin: 0 }}>Da gestire</h1>
        <div className="tabs">
          <button className={tab === "da_gestire" ? "active" : ""} onClick={() => setTab("da_gestire")} data-testid="filter-status-da-gestire">Da gestire</button>
          <button className={tab === "gestita" ? "active" : ""} onClick={() => setTab("gestita")} data-testid="filter-status-gestite">Gestite</button>
          <button className={tab === "tutte" ? "active" : ""} onClick={() => setTab("tutte")} data-testid="filter-status-tutte">Tutte</button>
        </div>
        <button className={"btn sm" + (soloDomande ? " primary" : "")} onClick={() => setSoloDomande(!soloDomande)}
          title="Mail che fanno una domanda o chiedono qualcosa" data-testid="filter-solo-domande">❓ Da rispondere</button>
        <div className="spacer" />
        <button className="btn sm primary" onClick={() => setCompose(true)} data-testid="compose-new-email-btn">✏️ Scrivi</button>
        <button className="btn sm" onClick={openDrafts} data-testid="open-drafts-btn">📝 Bozze{draftCount > 0 ? ` (${draftCount})` : ""}</button>
        <button className="btn sm" onClick={sync} data-testid="sync-gmail-btn">🔄 Sincronizza</button>
        <button className="btn sm" data-testid="repair-emails-btn"
                title="Ripara in blocco le mail archiviate senza testo o con immagini non visibili (riscarica da Gmail)"
                onClick={async () => {
                  if (!window.confirm("Riparare in blocco tutte le mail archiviate senza corpo o senza immagini? Il lavoro gira in sottofondo: a fine corsa arriva una notifica in campanella.")) return;
                  const r = await api.post("/emails/repair-all");
                  window.alert(r.data.avviato ? `Riparazione avviata su ${r.data.candidate} mail: ti avviso in campanella quando ho finito.` : `Nessun avvio: ${r.data.motivo}.`);
                }}>🔧</button>
        <input type="date" value={since} onChange={(e) => setSince(e.target.value)} title="Importa storico da questa data" data-testid="import-history-date" style={{ maxWidth: 135 }} />
        <button className="btn sm" onClick={importHistory} title="Importa lo storico Gmail dalla data scelta" data-testid="import-history-btn">⬇ Storico</button>
      </div>

      <div className="toolbar" style={{ flexWrap: "wrap", marginBottom: 0 }}>
        {accCounts.length > 0 && (<span style={{ display: "contents" }} data-testid="account-chips">
          <button className={"btn sm" + (accountF === "" ? " primary" : "")} onClick={() => setAccountF("")} data-testid="account-chip-all">Tutte</button>
          {accCounts.map((a) => (
            <button key={a.account} className={"btn sm" + (accountF === a.account ? " primary" : "")}
              onClick={() => setAccountF(accountF === a.account ? "" : a.account)} data-testid={`account-chip-${a.account}`}>
              {a.account.replace("@casalisilvia.com", "@…")} <b>({a.count})</b>
            </button>
          ))}
        </span>)}
        <input style={{ flex: 1, minWidth: 180, maxWidth: 420 }} placeholder="🔍 Cerca: mittente, parola, ditta, allegato…"
          value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="inbox-search" />
        {qInput && <button className="btn sm" onClick={() => setQInput("")} data-testid="inbox-search-clear">✕</button>}
        {q && <span className="muted" style={{ fontSize: 12 }} data-testid="inbox-search-scope">
          🔎 {list.length} risultat{list.length === 1 ? "o" : "i"} in <strong>{tab === "da_gestire" ? "Da gestire" : tab === "gestita" ? "Gestite" : "tutte le email"}</strong>
          {tab !== "tutte" && <button className="btn sm" style={{ marginLeft: 8, padding: "1px 8px", fontSize: 11 }} onClick={() => setTab("tutte")} data-testid="inbox-search-all">cerca in tutte</button>}
        </span>}
      </div>
      {syncMsg && <p className="muted" style={{ margin: "4px 0 0", fontSize: 12 }} data-testid="sync-msg">{syncMsg}</p>}
      </div>

      {suggs.length > 0 && (
        <div className="panel" style={{ marginTop: 10, border: "1px solid rgba(45,212,191,.35)" }} data-testid="link-suggestions-panel">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>🤖 Elon suggerisce — mittenti da collegare a clienti</div>
          {suggs.slice(0, 3).map((s) => (
            <div key={s.pattern} className="toolbar" style={{ gap: 8, flexWrap: "wrap", padding: "6px 0", borderTop: "1px solid var(--border)" }} data-testid={`link-sugg-${s.pattern}`}>
              <span style={{ fontSize: 13 }}>
                <strong>{s.from_email}</strong> ti scrive spesso di <strong>{s.company}</strong>
                <span className="muted"> ({s.hits} email su {s.count} lo citano)</span>
                {s.pattern !== s.from_email && <span className="muted" style={{ fontSize: 11 }}> — collega tutto @{s.pattern}</span>}
              </span>
              <div className="spacer" />
              <button className="btn sm primary" onClick={() => acceptSugg(s)} data-testid={`link-sugg-accept-${s.pattern}`}>🔗 Collega</button>
              <button className="btn sm" onClick={() => ignoreSugg(s)} data-testid={`link-sugg-ignore-${s.pattern}`}>✕ Ignora</button>
            </div>
          ))}
          {suggs.length > 3 && <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>…e altri {suggs.length - 3} suggerimenti (appariranno man mano)</div>}
        </div>
      )}

      {/* Interruttore rapido: organizzative dello Studio ↔ tutte le altre (fuori dai filtri) */}
      <div className="toolbar" style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }} data-testid="studio-switch">
        <button
          onClick={() => setRelation(relation === "studio" ? "" : "studio")}
          data-testid="studio-switch-studio"
          style={{
            border: "none", cursor: "pointer", borderRadius: 999, padding: "11px 20px",
            fontSize: 14.5, fontWeight: 700, letterSpacing: .2,
            color: relation === "studio" ? "#0b1020" : "#c8a24a",
            background: relation === "studio" ? "linear-gradient(135deg,#e2bf6b,#b08d4f)" : "rgba(176,141,79,.14)",
            boxShadow: relation === "studio" ? "0 6px 18px rgba(176,141,79,.35)" : "inset 0 0 0 1.5px rgba(176,141,79,.5)",
          }}
        >
          🏛️ Organizzative Studio {counts ? `· ${counts.studio ?? 0}` : ""}
        </button>
        <button
          onClick={() => setRelation(relation === "non-studio" ? "" : "non-studio")}
          data-testid="studio-switch-altre"
          style={{
            border: "none", cursor: "pointer", borderRadius: 999, padding: "11px 20px",
            fontSize: 14.5, fontWeight: 700, letterSpacing: .2,
            color: relation === "non-studio" ? "#07211b" : "#2dd4bf",
            background: relation === "non-studio" ? "linear-gradient(135deg,#5eead4,#14b8a6)" : "rgba(45,212,191,.12)",
            boxShadow: relation === "non-studio" ? "0 6px 18px rgba(20,184,166,.35)" : "inset 0 0 0 1.5px rgba(45,212,191,.45)",
          }}
        >
          📥 Clienti e tutte le altre {counts ? `· ${counts.tutti - (counts.studio ?? 0)}` : ""}
        </button>
        {(relation === "studio" || relation === "non-studio") && (
          <button className="btn sm" onClick={() => setRelation("")} data-testid="studio-switch-reset">✕ Mostra tutto</button>
        )}
      </div>

      {(() => {
        const nActive = [channel, relation, companyF, q, sender, recipient, dateFrom, dateTo].filter(Boolean).length
          + [hasAtt, unassigned, starredF, unreadF].filter(Boolean).length;
        return (
          <button className="btn sm" style={{ marginTop: 8 }} onClick={() => setFiltersOpen(!filtersOpen)} data-testid="inbox-filters-toggle">
            {filtersOpen ? "▲" : "▼"} Filtri{nActive > 0 ? ` (${nActive} attivi)` : ""}
          </button>
        );
      })()}

      {filtersOpen && (<>
      <div className="toolbar" style={{ gap: 8, marginTop: 4 }}>
        <span className="muted">Canale:</span>
        <button className={"btn sm" + (channel === "" ? " primary" : "")} onClick={() => setChannel("")} data-testid="filter-channel-tutti">Tutti</button>
        <button className={"btn sm" + (channel === "email" ? " primary" : "")} onClick={() => setChannel("email")} data-testid="filter-channel-email">Email</button>
        <button className={"btn sm" + (channel === "whatsapp" ? " primary" : "")} onClick={() => setChannel("whatsapp")} data-testid="filter-channel-whatsapp">WhatsApp</button>
      </div>

      <div className="toolbar" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <span className="muted">Provenienza:</span>
        <button className={"btn sm" + (relation === "" ? " primary" : "")} onClick={() => setRelation("")} data-testid="filter-relation-tutti">Tutti{counts ? ` (${counts.tutti})` : ""}</button>
        <button className={"btn sm" + (relation === "clienti" ? " primary" : "")} onClick={() => setRelation("clienti")} data-testid="filter-relation-clienti">👤 Clienti{counts ? ` (${counts.clienti})` : ""}</button>
        <button className={"btn sm" + (relation === "altre" ? " primary" : "")} onClick={() => setRelation("altre")} data-testid="filter-relation-altre">Altre{counts ? ` (${counts.altre})` : ""}</button>
        <button className={"btn sm" + (relation === "newsletter" ? " primary" : "")} onClick={() => setRelation("newsletter")} data-testid="filter-relation-newsletter">📰 Newsletter/Notifiche{counts ? ` (${counts.newsletter})` : ""}</button>
        <button className={"btn sm" + (relation === "sconosciuti" ? " primary" : "")} onClick={() => setRelation("sconosciuti")} data-testid="filter-relation-sconosciuti">❓ Sconosciuti{counts ? ` (${counts.sconosciuti})` : ""}</button>
        <div className="spacer" />
        {(relation === "altre" || relation === "newsletter" || relation === "sconosciuti") && list.some((m) => !m.is_client) && (
          <button className="btn sm" onClick={archiveOthers} data-testid="archive-others-btn">Archivia tutte le Altre</button>
        )}
      </div>

      <div className="toolbar" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <span className="muted">Filtri rapidi:</span>
        <button className={"btn sm" + (hasAtt ? " primary" : "")} onClick={() => setHasAtt(!hasAtt)} data-testid="filter-has-attachments">📎 Con allegati{counts ? ` (${counts.con_allegati})` : ""}</button>
        <button className={"btn sm" + (unassigned ? " primary" : "")} onClick={() => setUnassigned(!unassigned)} data-testid="filter-unassigned">🗂️ Non assegnate{counts ? ` (${counts.non_assegnate})` : ""}</button>
        <button className={"btn sm" + (starredF ? " primary" : "")} onClick={() => setStarredF(!starredF)} data-testid="filter-starred">⭐ Importanti</button>
        <button className={"btn sm" + (unreadF ? " primary" : "")} onClick={() => setUnreadF(!unreadF)} data-testid="filter-unread">✉️ Non letti</button>
      </div>

      <div className="toolbar" style={{ gap: 8, marginTop: 6, flexWrap: "wrap" }}>
        <span className="muted">Azienda:</span>
        <select style={{ maxWidth: 280 }} value={companyF} onChange={(e) => setCompanyF(e.target.value)} data-testid="inbox-filter-company">
          <option value="">Tutte le aziende</option>
          {allCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {companyF && <span className="muted" style={{ fontSize: 12 }}>mostra ricevute e inviate di tutti i contatti dell'azienda</span>}
        <input style={{ maxWidth: 160 }} placeholder="Mittente" value={sender} onChange={(e) => setSender(e.target.value)} data-testid="inbox-filter-sender" />
        <input style={{ maxWidth: 160 }} placeholder="Destinatario" value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="inbox-filter-recipient" />
        <span className="muted">Dal</span>
        <DateIT style={{ maxWidth: 150 }} value={dateFrom} onChange={(v) => setDateFrom(v)} data-testid="inbox-filter-from" />
        <span className="muted">al</span>
        <DateIT style={{ maxWidth: 150 }} value={dateTo} onChange={(v) => setDateTo(v)} data-testid="inbox-filter-to" />
        {(q || sender || recipient || dateFrom || dateTo || channel || relation || hasAtt || unassigned || starredF || unreadF || companyF) && <button className="btn sm" onClick={() => { setQ(""); setQInput(""); setSender(""); setRecipient(""); setDateFrom(""); setDateTo(""); setChannel(""); setRelation(""); setHasAtt(false); setUnassigned(false); setStarredF(false); setUnreadF(false); setCompanyF(""); }} data-testid="inbox-clear-filters">Azzera filtri</button>}
      </div>

      {domainGroups.length > 0 && (
        <div className="toolbar" style={{ gap: 8, marginTop: 6 }}>
          <span className="muted">Seleziona in blocco per dominio mittente:</span>
          <select value={domainPick} onChange={(e) => { setDomainPick(e.target.value); selectDomain(e.target.value); }} data-testid="domain-select" style={{ maxWidth: 320 }}>
            <option value="">— scegli un mittente / dominio —</option>
            {domainGroups.map(([d, n]) => <option key={d} value={d}>{d} ({n})</option>)}
          </select>
          <span className="muted" style={{ fontSize: 12 }}>utile per archiviare newsletter e notifiche automatiche (Google, Instagram…)</span>
        </div>
      )}
      </>)}

      {selected.size > 0 && (
        <div className="toolbar" style={{ position: "sticky", top: 0, zIndex: 40, marginTop: 8, padding: "10px 12px", background: "var(--panel-solid)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.45)", flexWrap: "wrap", gap: 6 }} data-testid="bulk-bar">
          <strong data-testid="bulk-count">{selected.size} selezionati</strong>
          <div className="spacer" />
          <button className="btn sm" onClick={() => bulk("handle")} data-testid="bulk-handle">Segna gestite</button>{" "}
          <button className="btn sm" onClick={openAssign} data-testid="bulk-assign-open">Abbina a cartella</button>{" "}
          <button className="btn sm" onClick={() => bulk("reopen")} data-testid="bulk-reopen">Riporta in Da gestire</button>{" "}
          <button className="btn sm danger" onClick={() => bulk("delete")} data-testid="bulk-delete">Elimina</button>{" "}
          <button className="btn sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear">Deseleziona</button>
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginTop: 4 }}>
      <div style={{ width: openMsg ? "32%" : "100%", minWidth: openMsg ? 300 : undefined, flexShrink: 0 }}>
      {openMsg && (() => {
        const rows = displayList.slice(0, visible).filter((m) => m._dir !== "out");
        let lastDay = null; const out = [];
        rows.forEach((m) => {
          const d = dayLabel(m.received_at);
          if (d !== lastDay) {
            lastDay = d;
            out.push(<div key={"d" + d + m.id} style={{ padding: "8px 10px 4px", fontSize: 11, letterSpacing: 1, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase" }}>{d}</div>);
          }
          out.push(
            <div key={m.id} onClick={() => openInline(m)} data-testid={`compact-row-${m.id}`}
              style={{ padding: "8px 10px", borderBottom: "1px solid var(--border)", cursor: "pointer", borderRadius: 8,
                       background: openMsg?.id === m.id ? "rgba(56,189,248,.10)" : "transparent" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleOne(m.id)} onClick={(e) => e.stopPropagation()} data-testid={`select-${m.id}`} />
                <span className="from-name cell-ellipsis" style={{ flex: 1, minWidth: 0 }}>{!m.read && <span style={{ color: "#38bdf8" }}>● </span>}{m.from_name || m.from_email}</span>
                <span className="muted" style={{ fontSize: 11, flexShrink: 0 }}>{m.received_at ? new Date(m.received_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : ""}</span>
              </div>
              <div className="cell-ellipsis" style={{ fontWeight: m.read ? 400 : 700, fontSize: 13 }}>{m.attachments_count > 0 && <span className="att-badge" style={{ marginLeft: 0, marginRight: 6 }} title={(m.attachment_names || []).join(", ") || "Con allegati"} data-testid={`att-badge-${m.id}`}>📎 {m.attachments_count}</span>}{m.urgente && "🚨 "}{m.subject || (m.channel === "whatsapp" ? "(messaggio WhatsApp)" : "(senza oggetto)")}</div>
              {m.is_client && <span style={{ fontSize: 11, color: m.category === "collegato" ? "#2dd4bf" : "var(--green)" }}>{m.category === "collegato" ? `🔗 per ${m.client_name}` : `👤 ${m.client_name || "Cliente"}`}</span>}
            </div>
          );
        });
        return (
          <div className="panel" style={{ padding: 4, maxHeight: "80vh", overflowY: "auto" }} data-testid="compact-list">
            {out.length === 0 && <div className="empty">Nessun messaggio.</div>}
            {out}
            {visible < displayList.length && <button className="btn sm" style={{ margin: 8 }} onClick={() => setVisible((v) => v + 50)} data-testid="compact-load-more">Altri 50</button>}
          </div>
        );
      })()}
      {!openMsg && (<>
      <table className="inbox-table">
        <thead><tr>
          <th style={{ width: 36 }}><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" /></th>
          <th style={{ width: 90 }}>Canale</th><th style={{ width: 210 }}>Mittente</th><th>Oggetto / Messaggio</th><th style={{ width: 116 }}>Ricevuta</th><th style={{ width: 120 }}>Stato</th><th style={{ width: 264 }}></th>
        </tr></thead>
        <tbody>
          {(() => {
            const rows = displayList.slice(0, visible);
            let lastDay = null;
            const out = [];
            rows.forEach((m) => {
              const d = dayLabel(m._dir === "out" ? m.sent_at : m.received_at);
              if (d !== lastDay) {
                lastDay = d;
                out.push(<tr key={`day-${d}-${m.id}`}><td colSpan={7} style={{ padding: "8px 10px 4px", fontSize: 11.5, letterSpacing: 1, fontWeight: 700, color: "var(--muted)", textTransform: "uppercase", borderBottom: "1px solid var(--border)" }} data-testid={`day-header-${d}`}>{d}</td></tr>);
              }
              out.push(m._dir === "out" ? (
            <tr key={m.id} className="email-row" style={{ opacity: .82 }} data-testid={`sent-row-${m.id}`}>
              <td></td>
              <td><span className="pill" style={{ background: "rgba(125,211,252,.15)", color: "#7dd3fc", border: "1px solid rgba(125,211,252,.35)" }}>↑ Inviata</span></td>
              <td><span className="from-name cell-ellipsis">A: {Array.isArray(m.to) ? m.to.join(", ") : m.to}</span>{m.cc && m.cc.length > 0 && <span className="muted cell-ellipsis">CC: {m.cc.join(", ")}</span>}</td>
              <td><span className="cell-ellipsis">{m.subject || "(senza oggetto)"}</span></td>
              <td className="muted">{fmt(m.sent_at)}</td>
              <td><span className="pill gestita">{m.channel === "whatsapp" ? "WhatsApp inviato" : "Inviata"}</span></td>
              <td></td>
            </tr>
          ) : (
            <tr key={m.id} className={`email-row ${m.status === "da_gestire" ? "unread" : ""}`}
                style={openMsg?.id === m.id ? { background: "rgba(56,189,248,.10)" } : undefined}>
              <td><input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleOne(m.id)} data-testid={`select-${m.id}`} /></td>
              <td>
                <button onClick={async () => { await api.post(`/emails/${m.id}/star`, { starred: !m.starred }); load(); }} title="Importante" data-testid={`star-${m.id}`} style={{ background: "none", border: "none", cursor: "pointer", color: m.starred ? "#f59e0b" : "var(--muted)", fontSize: 16 }}>{m.starred ? "★" : "☆"}</button>{" "}
                <span className={"pill " + (m.channel === "whatsapp" ? "gestita" : "")} data-testid={`channel-badge-${m.id}`}>{m.channel === "whatsapp" ? "WhatsApp" : "Email"}</span>
              </td>
              <td><a href={`/email/${m.id}`} onClick={(e) => { e.preventDefault(); openInline(m); }} data-testid={`open-inline-${m.id}`}>
                  <span className="from-name cell-ellipsis">{!m.read && <span style={{ color: "#38bdf8", marginRight: 4 }} title="Non letta">●</span>}{m.from_name || m.from_email}</span>
                  <span className="muted cell-ellipsis">{m.from_email}</span>
                  <span className="compact-subject cell-ellipsis" style={{ fontWeight: m.read ? 400 : 700 }}>{m.attachments_count > 0 && <span className="att-badge" style={{ marginLeft: 0, marginRight: 6 }} title={(m.attachment_names || []).join(", ") || "Con allegati"} data-testid={`att-badge-${m.id}`}>📎 {m.attachments_count}</span>}{m.urgente && "🚨 "}{m.subject || (m.channel === "whatsapp" ? "(messaggio WhatsApp)" : "(senza oggetto)")}</span></a>
                {m.is_client
                  ? (m.category === "collegato"
                    ? <span className="pill" style={{ background: "rgba(45,212,191,.14)", color: "#2dd4bf", border: "1px solid rgba(45,212,191,.4)", fontSize: 11, marginTop: 2, display: "inline-block" }} title="Mittente collegato: scrive per conto di questo cliente" data-testid={`linked-badge-${m.id}`}>🔗 per {m.client_name}</span>
                    : <span className="pill" style={{ background: "rgba(34,197,94,.15)", color: "var(--green)", border: "1px solid rgba(34,197,94,.35)", fontSize: 11, marginTop: 2, display: "inline-block" }} data-testid={`client-badge-${m.id}`}>👤 {m.client_name || "Cliente"}</span>)
                  : <span className="pill" style={{ background: "rgba(148,163,184,.12)", color: "var(--muted)", border: "1px solid var(--border)", fontSize: 11, marginTop: 2, display: "inline-block" }} data-testid={`other-badge-${m.id}`}>{m.category === "newsletter" ? "📰 Newsletter" : "❓ Sconosciuto"}</span>}
              </td>
              <td><a href={`/email/${m.id}`} onClick={(e) => { e.preventDefault(); openInline(m); }} className="cell-ellipsis" style={{ fontWeight: m.read ? 400 : 700 }}>{m.urgente && <span title={m.urgente_motivo || "Segnalata urgente dall'AI"} data-testid={`urgent-badge-${m.id}`}>🚨 </span>}{m.attachments_count > 0 && <span className="att-badge" style={{ marginLeft: 0, marginRight: 6 }} title={(m.attachment_names || []).join(", ") || "Con allegati"} data-testid={`att-badge-${m.id}`}>📎 {m.attachments_count}</span>}{m.subject || (m.channel === "whatsapp" ? "(messaggio WhatsApp)" : "(senza oggetto)")}</a><div className="muted cell-ellipsis snippet-line">{m.snippet}</div></td>
              <td className="muted">{fmt(m.received_at)}</td>
              <td><span className={`pill ${m.status}`}>{m.status === "da_gestire" ? "Da gestire" : "Gestita"}{m.handled_reason ? ` · ${m.handled_reason}` : ""}</span>
                {m.ha_domanda && <span className="pill" style={{ background: "rgba(249,115,22,.18)", color: "#fb923c", border: "1px solid rgba(249,115,22,.45)", fontSize: 11, marginTop: 3, display: "inline-block" }} title="Questa mail fa una domanda: va risposta" data-testid={`domanda-badge-${m.id}`}>❓ Da rispondere</span>}
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <button className="btn sm" onClick={() => openTask(m)} data-testid={`assign-task-${m.id}`}>+ Incarico</button>{" "}
                {m.status === "da_gestire"
                  ? <button className="btn sm" onClick={() => handleMsg(m)} title="Niente da fare: segna gestita" data-testid={`mark-handled-${m.id}`}>✓ Gestita</button>
                  : <button className="btn sm" onClick={() => reopenMsg(m)} data-testid={`reopen-${m.id}`}>Riapri</button>}{" "}
                <button className="btn sm danger" onClick={() => deleteMsg(m)} data-testid={`delete-msg-${m.id}`}>Elimina</button>
              </td>
            </tr>
          ));
            });
            return out;
          })()}
          {displayList.length === 0 && <tr><td colSpan={7}><div className="empty">Nessun messaggio in questa vista.</div></td></tr>}
        </tbody>
      </table>

      {displayList.length > 0 && (
        <div className="toolbar" style={{ justifyContent: "center", gap: 14, marginTop: 12 }} data-testid="inbox-pagination">
          <span className="muted" style={{ fontSize: 13 }}>Mostrati <b>{Math.min(visible, displayList.length)}</b> di <b>{displayList.length}</b></span>
          {visible < displayList.length && (
            <button className="btn sm" onClick={() => setVisible((v) => v + 50)} data-testid="inbox-load-more">Altri 50</button>
          )}
        </div>
      )}
      </>)}
      </div>

      {openMsg && (
        <div style={{ flex: 1, minWidth: 0, position: "sticky", top: 104 }} data-testid="inline-detail">
          <div className="panel" style={{ padding: 14 }}>
            <div className="toolbar" style={{ gap: 6, flexWrap: "wrap" }}>
              <button className="btn sm" onClick={closeDetail} data-testid="inline-back">← Torna alla lista</button>
              {openMsg.status === "da_gestire" && <button className="btn sm primary" onClick={async () => { await handleMsg(openMsg); closeDetail(); }} data-testid="inline-handle">✓ Niente da fare</button>}
              {openMsg.channel === "whatsapp"
                ? <Link className="btn sm" to={`/email/${openMsg.id}`} data-testid="inline-reply">↩️ Rispondi</Link>
                : (<>
                  <button className="btn sm" onClick={() => setMsgModal("reply")} data-testid="inline-reply">↩️ Rispondi</button>
                  <button className="btn sm" onClick={() => setMsgModal("replyall")} data-testid="inline-reply-all">↩↩ Tutti</button>
                  <button className="btn sm" onClick={() => setMsgModal("forward")} data-testid="inline-forward">➡️ Inoltra</button>
                </>)}
              {openMsg.channel !== "whatsapp" && <button className="btn sm danger" onClick={() => { setBlockScope("domain"); setBlockOpen(true); }} title="Blocca mittente ed elimina le sue email" data-testid="inline-block">⛔ Blocca</button>}
              <button className="btn sm danger" onClick={async () => { await deleteMsg(openMsg); closeDetail(); }} data-testid="inline-delete">🗑</button>
              <div className="spacer" />
              <Link className="btn sm" to={`/email/${openMsg.id}`} title="Apri la pagina completa (rispondi, thread, ripara…)" data-testid="inline-open-full">↗ Completa</Link>
              <button className="btn sm" onClick={closeDetail} data-testid="inline-close">✕</button>
            </div>
            <h3 style={{ margin: "10px 0 4px" }}>{openMsg.subject || "(senza oggetto)"}</h3>
            <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>
              Da <strong>{openMsg.from_name || openMsg.from_email}</strong> &lt;{openMsg.from_email}&gt;
              {openMsg.account && <> · a {openMsg.account}</>} · {openMsg.received_at ? new Date(openMsg.received_at).toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" }) : ""}
            </div>
            {openMsg.body_html
              ? <iframe title="corpo-email" srcDoc={openMsg.body_html} sandbox="allow-same-origin" style={{ width: "100%", height: "58vh", border: "1px solid var(--border)", borderRadius: 10, background: "#fff" }} />
              : <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, maxHeight: "58vh", overflow: "auto" }}>{openMsg.body_text || openMsg.snippet || "(nessun contenuto)"}</pre>}
          </div>
        </div>
      )}

      {openMsg && (
        <div style={{ width: 300, flexShrink: 0, position: "sticky", top: 104 }} data-testid="inline-info">
          <div className="panel" style={{ padding: 12, marginBottom: 10 }} data-testid="inline-actions">
            <div style={{ fontWeight: 600, marginBottom: 8 }}>⚡ Azioni</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <button className="btn sm" onClick={openAssignOne} data-testid="inline-assign">📁 Cartella</button>
              <button className="btn sm" onClick={toggleStar} data-testid="inline-star">{openMsg.starred ? "⭐ Importante" : "☆ Importante"}</button>
              <button className="btn sm" onClick={openEvent} data-testid="inline-event">📅 Evento</button>
              <button className="btn sm" onClick={openWorkTime} title="Registra il tempo dedicato: entra nelle ore e nel margine del cliente" data-testid="inline-worktime">⏱ Tempo</button>
              <button className="btn sm" onClick={() => openTask(openMsg)} data-testid="inline-task">🚩 Incarico</button>
              <button className="btn sm" onClick={createLead} title="Crea un lead in pipeline da questo mittente" data-testid="inline-create-lead">🎯 Crea lead</button>
            </div>
            {(openMsg.client_name || openMsg.company_id || openMsg.folder_name) && (
              <div style={{ marginTop: 10, fontSize: 12.5, borderTop: "1px solid var(--border)", paddingTop: 8, display: "grid", gap: 4 }} data-testid="inline-linked">
                {openMsg.client_name && <div>👤 Anagrafica: <strong>{openMsg.client_name}</strong></div>}
                {openMsg.company_id && (
                  <div>🏢 Cliente: <Link to={`/aziende/${openMsg.company_id}`} data-testid="inline-linked-company">{(allCompanies.find((c) => c.id === openMsg.company_id) || {}).name || "apri scheda"}</Link></div>
                )}
                {openMsg.folder_name && <div>📁 Cartella: <strong>{openMsg.folder_name}</strong>{openMsg.folder_project ? <span className="muted"> · {openMsg.folder_project}</span> : null}</div>}
              </div>
            )}
          </div>
          {(openMsg.attachments || []).length > 0 && (
            <div className="panel" style={{ padding: 12, marginBottom: 10 }} data-testid="inline-attachments">
              <div style={{ fontWeight: 600, marginBottom: 6 }}>📎 Allegati ({openMsg.attachments.length})</div>
              {openMsg.attachments.map((a, i) => (
                <button key={i} className="btn sm" style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  onClick={() => downloadAtt({ ...a, index: a.index ?? i })} data-testid={`inline-att-${i}`}>
                  ⬇️ {a.filename || `allegato ${i + 1}`}
                </button>
              ))}
            </div>
          )}
          <SenderCard email={openMsg.from_email} senderName={openMsg.from_name} onChanged={() => { load(); }} />
        </div>
      )}
      </div>

      {assignOpen && (
        <Modal title={assignOneId ? "Abbina questa email a una cartella" : `Abbina ${selected.size} email a una cartella`} onClose={() => { setAssignOpen(false); setAssignOneId(null); }}>
          <div className="field"><label>Cliente</label>
            <select value={selCompany} onChange={(e) => onCompany(e.target.value)} data-testid="bulk-assign-company">
              <option value="">— seleziona —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {selCompany && (
            <div className="field"><label>Progetto</label>
              <select value={selProject} onChange={(e) => onProject(e.target.value)} data-testid="bulk-assign-project">
                <option value="">— seleziona —</option>
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
          )}
          {selProject && (
            <div className="field"><label>Cartella</label>
              <select value={selFolder} onChange={(e) => setSelFolder(e.target.value)} data-testid="bulk-assign-folder">
                <option value="">— seleziona —</option>
                {folders.filter((f) => showClosedF || !f.closed).map((f) => <option key={f.id} value={f.id}>{f.closed ? `🔒 ${f.name} (chiusa)` : f.name}</option>)}
              </select>
              {folders.some((f) => f.closed) && (
                <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6, fontSize: 13 }}>
                  <input type="checkbox" checked={showClosedF} onChange={(e) => setShowClosedF(e.target.checked)} data-testid="assign-show-closed" />
                  <span className="muted">Mostra anche le cartelle chiuse ({folders.filter((f) => f.closed).length})</span>
                </label>
              )}
            </div>
          )}
          <p className="muted" style={{ fontSize: 12 }}>Le email verranno segnate come gestite e gli allegati PDF salvati nell'archivio del cliente.</p>
          <div className="modal-actions">
            <button className="btn" onClick={() => { setAssignOpen(false); setAssignOneId(null); }}>Annulla</button>
            <button className="btn primary" onClick={confirmAssign} disabled={!selFolder} data-testid="bulk-assign-confirm">Abbina</button>
          </div>
        </Modal>
      )}

      {taskModal && (
        <Modal title="Assegna incarico" onClose={() => setTaskModal(null)}>
          <p className="muted" style={{ fontSize: 12 }}>Da: {taskModal.email.from_name || taskModal.email.from_email}</p>
          <div className="field"><label>Cosa fare *</label><input value={taskModal.title} onChange={(e) => setTaskModal({ ...taskModal, title: e.target.value })} placeholder="es. Richiamare il cliente" data-testid="task-title" /></div>
          <div className="field"><label>Scadenza</label><DateIT value={taskModal.due} onChange={(v) => setTaskModal({ ...taskModal, due: v })} data-testid="task-due" /></div>
          <div className="field"><label>Nota</label><textarea rows={3} value={taskModal.note} onChange={(e) => setTaskModal({ ...taskModal, note: e.target.value })} placeholder="Appunti su questa mail / incarico…" data-testid="task-note" /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setTaskModal(null)}>Annulla</button>
            <button className="btn primary" onClick={saveTask} disabled={!taskModal.title} data-testid="task-save">Crea incarico</button>
          </div>
        </Modal>
      )}
      {msgModal && openMsg && (
        <EmailComposer mode={msgModal} original={openMsg} onClose={() => setMsgModal(null)} onSent={() => { setMsgModal(null); load(); }} />
      )}

      {evt && openMsg && (
        <Modal title="Crea evento in calendario" onClose={() => setEvt(null)}>
          <div className="field"><label>Titolo *</label><input value={evt.title} onChange={(e) => setEvt({ ...evt, title: e.target.value })} data-testid="inline-event-title" /></div>
          <div className="grid-2">
            <div className="field"><label>Data *</label><input type="date" value={evt.date} onChange={(e) => setEvt({ ...evt, date: e.target.value })} data-testid="inline-event-date" /></div>
            <div className="field"><label>Ora *</label><input type="time" value={evt.time} onChange={(e) => setEvt({ ...evt, time: e.target.value })} data-testid="inline-event-time" /></div>
          </div>
          <div className="grid-2">
            <div className="field"><label>Durata (minuti)</label><input type="number" min="10" step="5" value={evt.dur} onChange={(e) => setEvt({ ...evt, dur: e.target.value })} data-testid="inline-event-dur" /></div>
            <div className="field"><label>Dove crearlo</label>
              <select value={evt.target} onChange={(e) => setEvt({ ...evt, target: e.target.value })} data-testid="inline-event-target">
                <option value="crm">Calendario CRM (appuntamento)</option>
                <option value="google">Google Calendar</option>
                <option value="both">Entrambi</option>
              </select>
            </div>
          </div>
          {(evt.target === "google" || evt.target === "both") && openMsg.channel !== "whatsapp" && (
            <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, marginBottom: 10 }}>
              <input type="checkbox" checked={evt.invite} onChange={(e) => setEvt({ ...evt, invite: e.target.checked })} data-testid="inline-event-invite" />
              Invita {openMsg.from_email} (riceve l'invito da Google Calendar)
            </label>
          )}
          <div className="field"><label>Note</label><textarea rows={2} value={evt.desc} onChange={(e) => setEvt({ ...evt, desc: e.target.value })} data-testid="inline-event-desc" /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setEvt(null)}>Annulla</button>
            <button className="btn primary" onClick={saveEvent} disabled={!evt.title.trim() || !evt.date || !evt.time} data-testid="inline-event-save">Crea evento</button>
          </div>
        </Modal>
      )}

      {workTime && openMsg && (
        <Modal title="⏱ Registra tempo di lavorazione" onClose={() => setWorkTime(null)}>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>Il tempo verrà conteggiato nelle <strong>ore dedicate</strong> e quindi nel <strong>margine del cliente</strong> (pagina Margini clienti). Non viene inviato nulla al cliente.</p>
          <div className="field"><label>Cliente *</label>
            <select value={workTime.company_id} onChange={(e) => setWorkTime({ ...workTime, company_id: e.target.value })} data-testid="worktime-company">
              <option value="">— seleziona il cliente —</option>
              {allCompanies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid-2">
            <div className="field"><label>Data</label><input type="date" value={workTime.date} onChange={(e) => setWorkTime({ ...workTime, date: e.target.value })} data-testid="worktime-date" /></div>
            <div className="field"><label>Ora</label><input type="time" value={workTime.time} onChange={(e) => setWorkTime({ ...workTime, time: e.target.value })} data-testid="worktime-time" /></div>
          </div>
          <div className="field"><label>Durata (minuti)</label>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {[15, 30, 60, 120].map((m) => (
                <button key={m} className={"btn sm" + (Number(workTime.dur) === m ? " primary" : "")} onClick={() => setWorkTime({ ...workTime, dur: m })} data-testid={`worktime-dur-${m}`}>{m >= 60 ? `${m / 60}h` : `${m} min`}</button>
              ))}
              <input type="number" min="5" step="5" value={workTime.dur} onChange={(e) => setWorkTime({ ...workTime, dur: e.target.value })} style={{ maxWidth: 90 }} data-testid="worktime-dur" />
            </div>
          </div>
          <div className="field"><label>Descrizione</label><input value={workTime.title} onChange={(e) => setWorkTime({ ...workTime, title: e.target.value })} data-testid="worktime-title" /></div>
          <div className="modal-actions">
            <button className="btn" onClick={() => setWorkTime(null)}>Annulla</button>
            <button className="btn primary" onClick={saveWorkTime} disabled={!workTime.company_id || !workTime.date || !workTime.time || !Number(workTime.dur)} data-testid="worktime-save">Registra tempo</button>
          </div>
        </Modal>
      )}

      {blockOpen && openMsg && (() => {
        const domain = (openMsg.from_email || "").split("@")[1] || "";
        const isGeneric = GENERIC_DOMAINS.has(domain.toLowerCase());
        return (
        <Modal title="Blocca ed elimina" onClose={() => setBlockOpen(false)}>
          <p>Non vuoi più ricevere messaggi da <strong>{openMsg.from_email}</strong>?</p>
          <div className="field" style={{ marginTop: 4 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: isGeneric ? "not-allowed" : "pointer", opacity: isGeneric ? .5 : 1 }}>
              <input type="radio" name="inline-block-scope" checked={blockScope === "domain" && !isGeneric} disabled={isGeneric} onChange={() => setBlockScope("domain")} data-testid="inline-block-scope-domain" />
              <span>Blocca <strong>tutto il dominio @{domain || "—"}</strong> <span className="muted">(consigliato per pubblicità)</span></span>
            </label>
            <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer", marginTop: 6 }}>
              <input type="radio" name="inline-block-scope" checked={blockScope === "email" || isGeneric} onChange={() => setBlockScope("email")} data-testid="inline-block-scope-email" />
              <span>Blocca <strong>solo questo indirizzo</strong></span>
            </label>
            {isGeneric && <p className="muted" style={{ fontSize: 12, marginTop: 6 }}>⚠️ @{domain} è un dominio generico (caselle personali): si può bloccare solo il singolo indirizzo, non l'intero dominio.</p>}
          </div>
          <p className="muted" style={{ fontSize: 13 }}>Verranno <strong>eliminate dal CRM tutte le email passate</strong> {blockScope === "domain" && !isGeneric ? "di questo dominio" : "di questo mittente"} e quelle future saranno bloccate automaticamente.</p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setBlockOpen(false)} data-testid="inline-block-cancel">Annulla</button>
            <button className="btn danger" onClick={confirmBlock} data-testid="inline-block-confirm">Sì, blocca ed elimina</button>
          </div>
        </Modal>
        );
      })()}

      {compose && <EmailComposer mode="new" onClose={() => setCompose(false)} onSent={() => setCompose(false)} />}
      {resumeDraft && (resumeDraft.channel === "whatsapp"
        ? <WaDraftModal draft={resumeDraft} onClose={() => setResumeDraft(null)} onSent={() => setResumeDraft(null)} />
        : <EmailComposer mode="new" draft={resumeDraft} onClose={() => setResumeDraft(null)} onSent={() => setResumeDraft(null)} />)}

      {drafts && (
        <Modal title={`Bozze salvate (${drafts.length})`} onClose={() => setDrafts(null)}>
          {drafts.length === 0 && <p className="muted">Nessuna bozza salvata. Nel composer usa "💾 Salva bozza" per riprenderla più tardi.</p>}
          <table className="table" style={{ width: "100%" }}>
            <tbody>
              {drafts.map((d) => (
                <tr key={d.id} data-testid={`draft-row-${d.id}`}>
                  <td>
                    {d.channel === "whatsapp"
                      ? <><b>💬 WhatsApp a {d.wa_name || d.to}</b><br /><span className="muted" style={{ fontSize: 12 }}>{(d.body_text || "").slice(0, 70) || "(vuota)"}</span></>
                      : <><b>{d.subject || "(senza oggetto)"}</b><br /><span className="muted" style={{ fontSize: 12 }}>A: {d.to || "—"}</span></>}
                  </td>
                  <td className="muted" style={{ whiteSpace: "nowrap", fontSize: 12 }}>{d.updated_at ? new Date(d.updated_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : ""}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="btn sm primary" onClick={() => { setResumeDraft(d); setDrafts(null); }} data-testid={`draft-resume-${d.id}`}>Riprendi</button>{" "}
                    {d.channel !== "whatsapp" && <><button className="btn sm" onClick={() => { openComposerWindow({ draft: d.id }); setDrafts(null); }} data-testid={`draft-window-${d.id}`} title="Apri in una finestra separata">↗ Finestra</button>{" "}</>}
                    <button className="btn sm danger" onClick={() => deleteDraft(d.id)} data-testid={`draft-delete-${d.id}`}>Elimina</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
}
